import { useEffect, useState } from "react";
import {
  MessageSquare,
  Copy,
  Check,
  Sparkles,
} from "lucide-react";
import { SETTORI } from "../../lib/kb";
import { api } from "../../lib/api";
import { EmptyHistory } from "../../components/illustrations/EmptyIllustrations";
import CommercialPanel from "../../components/CommercialPanel";
import NoteClienteRiquadro from "../../components/cliente/NoteClienteRiquadro";
import SendMailButton from "../../components/cliente/SendMailButton";
import ChecklistVerificaOfferta from "../../components/cliente/ChecklistVerificaOfferta";
import CustomerAutoProfile from "../../components/CustomerAutoProfile";
import ConversationThread from "../../components/ConversationThread";
import TotaleOffertaCard from "../../components/TotaleOffertaCard";

const STATUS_COLOR = {
  awaiting_info: { bg: "#FEF3C7", color: "#92400E" },
  matching: { bg: "#EFF6FF", color: "#1D4ED8" },
  completed: { bg: "#DCFCE7", color: "#166534" },
};

function Pill({ children, tone = "default", ...rest }) {
  const byTone = {
    accent: {
      background: "var(--accent-subtle)",
      color: "var(--accent-primary)",
      border: "1px solid #DBEAFE",
    },
    warn: {
      background: "#FEF3C7",
      color: "#92400E",
      border: "1px solid #FDE68A",
    },
    positive: {
      background: "#DCFCE7",
      color: "#166534",
      border: "1px solid #BBF7D0",
    },
    default: {
      background: "var(--bg-surface)",
      color: "var(--text-secondary)",
      border: "1px solid var(--border-subtle)",
    },
  };
  const styles = byTone[tone] || byTone.default;
  return (
    <span
      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium"
      style={styles}
      {...rest}
    >
      {children}
    </span>
  );
}

/** Typewriter effect for the generated message */
function Typewriter({ text, speed = 18, onDone }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    setI(0);
    if (!text) return;
    let idx = 0;
    const timer = setInterval(() => {
      idx += 1;
      setI(idx);
      if (idx >= text.length) {
        clearInterval(timer);
        onDone?.();
      }
    }, speed);
    return () => clearInterval(timer);
  }, [text, speed, onDone]);
  const shown = text ? text.slice(0, i) : "";
  const isDone = i >= (text?.length || 0);
  return (
    <span className="whitespace-pre-wrap">
      {shown}
      {!isDone && <span className="tq-caret" />}
    </span>
  );
}

export default function ClientePane({
  session,
  onGenerateMessage,
  generating,
  message,
  toast,
  onVociUpdate,
}) {
  const cliente = session?.cliente || {};
  const offerId = session?.session_id;
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [copied, setCopied] = useState(false);
  const [justGenerated, setJustGenerated] = useState(false);

  useEffect(() => {
    if (cliente?.nome_azienda) {
      setLoadingHistory(true);
      api
        .byCustomer(cliente.nome_azienda)
        .then((d) => {
          setHistory(
            (d.items || []).filter((s) => s.session_id !== session?.session_id)
          );
        })
        .catch(() => setHistory([]))
        .finally(() => setLoadingHistory(false));
    } else {
      setHistory([]);
    }
  }, [cliente?.nome_azienda, session?.session_id]);

  // Trigger typewriter whenever a new message arrives
  useEffect(() => {
    if (message) setJustGenerated(true);
  }, [message]);

  const copy = async () => {
    if (!message) return;
    await navigator.clipboard.writeText(message);
    setCopied(true);
    toast?.("Copiato negli appunti");
    setTimeout(() => setCopied(false), 1800);
  };

  const sectorLabel = SETTORI[cliente.settore_presunto];
  const sectorEstimated = !!cliente.settore_presunto && !cliente.settore_confirmed;

  return (
    <aside
      className="flex flex-col h-full overflow-hidden bg-white"
      data-testid="cliente-pane"
    >
      <header className="px-3 pt-4 pb-3">
        <div className="tq-section-label mb-2">Cliente</div>
        <h2
          className="text-[15px] font-semibold leading-tight break-words"
          style={{ color: "var(--text-primary)" }}
          data-testid="cliente-nome"
        >
          {cliente.nome_azienda || (
            <span style={{ color: "var(--text-muted)" }}>
              Nessuna richiesta attiva
            </span>
          )}
        </h2>
        {cliente.nome_azienda && (
          <div className="mt-2 flex items-center gap-1 flex-wrap">
            {sectorLabel && (
              <Pill tone="accent" data-testid="cliente-settore">
                {sectorLabel}
                {sectorEstimated && (
                  <span
                    className="ml-1 text-[10px]"
                    style={{ opacity: 0.7 }}
                  >
                    · stimato
                  </span>
                )}
              </Pill>
            )}
            {cliente.profilo && (
              <Pill
                tone={
                  String(cliente.profilo).toLowerCase().includes("acquisit") &&
                  !String(cliente.profilo).toLowerCase().includes("da ")
                    ? "positive"
                    : "warn"
                }
                data-testid="cliente-profilo"
              >
                {cliente.profilo}
              </Pill>
            )}
            {typeof cliente.confidenza === "number" && (
              <Pill data-testid="cliente-confidenza">
                {Math.round(cliente.confidenza * 100)}%
              </Pill>
            )}
          </div>
        )}
      </header>

      <div className="flex-1 overflow-auto tq-scroll px-3 pb-4 space-y-4">
        {/* R10 v0.5.8 — Totale offerta autocalcolato */}
        {session?.session_id && (
          <TotaleOffertaCard
            sessionId={session.session_id}
            voci={session?.voci || []}
            session={session}
          />
        )}

        {/* Commercial parameters + margin + shipping (TASK 4/5/6) */}
        {session?.session_id && (
          <CommercialPanel
            sessionId={session.session_id}
            session={session}
          />
        )}

        {/* R4 — checklist spostata in sidebar SX (InputPane) */}

        {/* Note libere + preferenze strutturate (R3 FIX G) ---------------- */}
        <section data-testid="cliente-preferenze">
          <div className="tq-section-label mb-2">Note cliente</div>
          <CustomerAutoProfile
            emailDomain={
              cliente?.email_domain ||
              (cliente?.email
                ? String(cliente.email).split("@").pop()
                : null)
            }
          />
          <NoteClienteRiquadro
            emailDomain={
              cliente?.email_domain ||
              (cliente?.email
                ? String(cliente.email).split("@").pop()
                : null) ||
              (cliente?.nome_azienda
                ? cliente.nome_azienda
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-|-$/g, "")
                : null)
            }
          />
        </section>

        {/* Quick message ------------------------------------------------- */}
        <section data-testid="cliente-messaggio">
          <div className="flex items-center justify-between mb-2">
            <div className="tq-section-label flex items-center gap-1.5">
              <MessageSquare className="h-3 w-3" />
              Messaggio rapido
            </div>
            <button
              type="button"
              onClick={() => {
                setJustGenerated(false);
                onGenerateMessage();
              }}
              disabled={generating || !session?.session_id}
              className="inline-flex items-center gap-1 px-2 h-7 rounded-md text-[12px] font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                color: "var(--accent-primary)",
                border: "1px solid #DBEAFE",
                background: "transparent",
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled)
                  e.currentTarget.style.background = "var(--accent-subtle)";
              }}
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
              data-testid="generate-message-btn"
            >
              <Sparkles className="h-3 w-3" />
              {generating
                ? "..."
                : message
                ? "Rigenera"
                : "Genera"}
            </button>
          </div>
          <div
            className="rounded-md p-2.5 text-[12px] leading-[18px] min-h-[72px] break-words whitespace-pre-wrap"
            style={{
              border: "1px solid var(--border-subtle)",
              background: "var(--bg-surface)",
              color: message ? "var(--text-primary)" : "var(--text-muted)",
              fontStyle: message ? "normal" : "italic",
            }}
            data-testid="message-textarea"
          >
            {generating ? (
              <span className="italic">Stesura in corso...</span>
            ) : message ? (
              justGenerated ? (
                <Typewriter text={message} onDone={() => setJustGenerated(false)} />
              ) : (
                message
              )
            ) : (
              "Premi Genera per richiedere i dati mancanti al cliente."
            )}
          </div>
          {message && !generating && (
            <div className="flex items-center gap-1.5 mt-2">
              <button
                type="button"
                onClick={copy}
                className="inline-flex items-center gap-1.5 px-2.5 h-8 rounded-md text-[12px] font-medium transition-colors"
                style={{
                  border: "1px solid var(--border-default)",
                  background: "var(--bg-surface)",
                  color: "var(--text-primary)",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "var(--bg-subtle)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "var(--bg-surface)")
                }
                data-testid="copy-message-btn"
              >
                {copied ? (
                  <>
                    <Check className="h-3 w-3" /> Copiato
                  </>
                ) : (
                  <>
                    <Copy className="h-3 w-3" /> Copia
                  </>
                )}
              </button>
              <SendMailButton
                offerId={offerId}
                defaultTo={cliente?.email || ""}
                subject={
                  cliente?.nome_azienda
                    ? `Offerta TechQuote — ${cliente.nome_azienda}`
                    : "Richiesta informazioni offerta"
                }
                body={message}
              />
              <span
                className="text-[11px] font-mono"
                style={{ color: "var(--text-muted)" }}
              >
                {message.length} car · {message.split("\n").filter(Boolean).length}{" "}
                {message.split("\n").filter(Boolean).length === 1
                  ? "riga"
                  : "righe"}
              </span>
            </div>
          )}
        </section>

        {/* R6 — Ping-pong assistito conversazionale */}
        {offerId && (
          <section data-testid="cliente-conversation">
            <ConversationThread
              offerId={offerId}
              onVociUpdate={onVociUpdate}
            />
          </section>
        )}
      </div>
    </aside>
  );
}
